import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI, etapasAPI, usersAPI, atividadesAPI, empresaAPI } from '../lib/api';
import { filtrarUsuariosAtribuicao } from '../lib/users';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FolderOpen,
  ChevronDown,
  ChevronRight,
  Filter,
  Search,
  XCircle,
  LayoutList,
  Kanban,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Plus,
  User,
  ArrowRightLeft,
  ClipboardList,
} from 'lucide-react';
import { cn } from '../lib/utils';
import ProjectTimeline from '../components/ProjectTimeline';
import KanbanBoard from '../components/KanbanBoard';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const formatCPF = (cpf) => {
  if (!cpf) return '-';
  if (cpf.length === 14) {
    return cpf.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

// modo="suspensos": mesma página (tabela, filtros, busca) listando só os projetos suspensos
const Projetos = ({ modo = 'andamento' }) => {
  const suspensos = modo === 'suspensos';
  const basePath = suspensos ? '/projetos/suspensos' : '/projetos';
  const navigate = useNavigate();
  const [searchParams] = React.useState(() => new URLSearchParams(window.location.search));
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [statusConfig, setStatusConfig] = useState([]);
  const [expandedProject, setExpandedProject] = useState(null);
  const [destaqueUrl, setDestaqueUrl] = useState(null);   // projeto aberto por link (?project=): rola até ele e destaca
  const [viewMode, setViewMode] = useState('table');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState(null);      // 'cliente_nome' | 'cliente_cpf' | etc
  const [sortDir, setSortDir] = useState('asc');   // 'asc' | 'desc'

  // Alterna ordenação clicando no cabeçalho
  // Ciclo: null → asc → desc → null (limpa)
  const handleSort = (campo) => {
    if (sortBy !== campo) {
      setSortBy(campo);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      // estava em desc → limpa ordenação
      setSortBy(null);
      setSortDir('asc');
    }
  };

  // Componente local: cabeçalho clicável com indicador de direção
  const SortableHead = ({ campo, children, className = '' }) => {
    const isActive = sortBy === campo;
    const Icon = isActive ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <TableHead className={className}>
        <button
          type="button"
          onClick={() => handleSort(campo)}
          className={`inline-flex items-center gap-1.5 hover:text-primary transition-colors select-none ${isActive ? 'text-primary font-bold' : ''}`}
          data-testid={`sort-${campo}`}
        >
          {children}
          <Icon className={`w-3 h-3 ${isActive ? '' : 'opacity-40'}`} />
        </button>
      </TableHead>
    );
  };
  const [atividadesCount, setAtividadesCount] = useState({}); // {projectId: count}
  const [exibirNumero, setExibirNumero] = useState(true);
  
  // Permissões
  const [canTransfer, setCanTransfer] = useState(false);
  const [canViewKanban, setCanViewKanban] = useState(true);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferProjectId, setTransferProjectId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  
  // Filters
  const [filters, setFilters] = useState({
    mes: null,
    ano: null,
    etapa_id: null,
    usuario_id: null,
    pendencia: null,
  });
  const [showFilters, setShowFilters] = useState(false);

  // Abrir projeto automaticamente se vier da URL
  useEffect(() => {
    const projectId = searchParams.get('project');
    if (projectId && projects.length > 0) {
      const project = projects.find(p => p.id === projectId);
      if (project) {
        setViewMode('table');
        setExpandedProject(projectId);
        setDestaqueUrl(projectId);
        window.history.replaceState({}, '', basePath);
      }
    }
  }, [searchParams, projects, basePath]);

  useEffect(() => {
    if (!destaqueUrl) return undefined;
    const rolar = setTimeout(() => {
      document.querySelector(`[data-testid="project-row-${destaqueUrl}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    const apagar = setTimeout(() => setDestaqueUrl(null), 4000);
    return () => { clearTimeout(rolar); clearTimeout(apagar); };
  }, [destaqueUrl]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('agrolink_token');
      // Projetos: só em andamento (os suspensos ficam na página própria)
      const params = suspensos
        ? { status: 'suspenso' }
        : { status: 'em_andamento', incluir_suspensos: false };
      
      // Adicionar filtros apenas se definidos
      if (filters.pendencia !== null) {
        params.pendencia = filters.pendencia;
      }
      if (filters.etapa_id) {
        params.etapa_id = filters.etapa_id;
      }
      if (filters.usuario_id) {
        params.usuario_id = filters.usuario_id;
      }
      if (filters.mes && filters.ano) {
        params.mes = filters.mes;
        params.ano = filters.ano;
      }
      
      const [projectsRes, etapasRes, usersRes, statusRes, transferRes, kanbanRes] = await Promise.all([
        projectsAPI.list(params),
        etapasAPI.list(),
        usersAPI.list().catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/status-projeto/ativos`, { 
          headers: { Authorization: `Bearer ${token}` } 
        }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/permissions/can-transfer-project`, { 
          headers: { Authorization: `Bearer ${token}` } 
        }).catch(() => ({ data: { can_transfer: false } })),
        axios.get(`${API_URL}/api/permissions/can-view-kanban`, { 
          headers: { Authorization: `Bearer ${token}` } 
        }).catch(() => ({ data: { can_view: true } })),
      ]);
      setProjects(projectsRes.data);
      setEtapas(etapasRes.data);
      setUsuarios(usersRes.data || []);
      setStatusConfig(statusRes.data || []);
      setCanTransfer(transferRes.data?.can_transfer || false);
      setCanViewKanban(kanbanRes.data?.can_view !== false);
      // Contadores de atividades pendentes por projeto (uma única chamada)
      atividadesAPI.countsByProject()
        .then((r) => setAtividadesCount(r.data || {}))
        .catch(() => setAtividadesCount({}));
      empresaAPI.getConfig()
        .then((r) => setExibirNumero(r.data?.exibir_numero_projeto !== false))
        .catch(() => setExibirNumero(true));
    } catch (error) {
      toast.error('Erro ao carregar projetos');
    } finally {
      setLoading(false);
    }
  }, [filters, suspensos]);
  
  const handleTransfer = async () => {
    if (!transferProjectId || !selectedUserId) {
      toast.error('Selecione um usuário');
      return;
    }
    
    try {
      const token = localStorage.getItem('agrolink_token');
      await axios.put(
        `${API_URL}/api/projects/${transferProjectId}/transfer`,
        { novo_usuario_id: selectedUserId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Projeto transferido com sucesso!');
      setShowTransferDialog(false);
      setTransferProjectId(null);
      setSelectedUserId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao transferir projeto');
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const calculateDuration = (dataInicio) => {
    const start = new Date(dataInicio);
    const now = new Date();
    const diff = Math.floor((now - start) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Função para determinar o status/cor de um projeto baseado nas configurações
  const getProjectStatus = (project) => {
    // Projeto suspenso tem badge dedicado, com contador congelado
    if (project.status === 'suspenso') {
      return { nome: 'Suspenso', cor: 'gray', cor_classe: '' };
    }

    if (!statusConfig || statusConfig.length === 0) {
      // Fallback se não houver configurações
      return { nome: '', cor_classe: '' };
    }

    const etapaNome = project.etapa_atual_nome || project.etapa_atual || '';
    const temPendencia = project.tem_pendencia || false;
    const diasProjeto = project.data_inicio ? calculateDuration(project.data_inicio) : 0;
    
    // Calcular dias sem comentário (usando última observação, ignorando marcador [SUSPENSO])
    const observacoesReais = (project.observacoes || []).filter(o => !(o.texto || '').trim().startsWith('[SUSPENSO]'));
    const ultimaObservacao = observacoesReais.length > 0 
      ? new Date(observacoesReais[observacoesReais.length - 1].data)
      : project.data_inicio ? new Date(project.data_inicio) : new Date();
    const diasSemComentario = Math.floor((new Date() - ultimaObservacao) / (1000 * 60 * 60 * 24));

    // Verificar cada status por ordem de prioridade (já vem ordenado do backend)
    for (const status of statusConfig) {
      let match = true;

      // Verificar condição de etapas
      if (status.condicao_etapas && status.condicao_etapas.length > 0) {
        if (!status.condicao_etapas.includes(etapaNome)) {
          match = false;
        }
      }

      // Verificar condição de pendência
      if (match && status.condicao_pendencia !== null) {
        if (status.condicao_pendencia !== temPendencia) {
          match = false;
        }
      }

      // Verificar dias sem comentário (mínimo - para Tratativa)
      if (match && status.condicao_dias_sem_comentario !== null && status.condicao_dias_sem_comentario !== undefined) {
        if (diasSemComentario < status.condicao_dias_sem_comentario) {
          match = false;
        }
      }

      // Verificar dias sem comentário (máximo - para OK)
      if (match && status.condicao_dias_sem_comentario_max !== null && status.condicao_dias_sem_comentario_max !== undefined) {
        if (diasSemComentario > status.condicao_dias_sem_comentario_max) {
          match = false;
        }
      }

      // Verificar dias máximos do projeto
      if (match && status.condicao_dias_max !== null && status.condicao_dias_max !== undefined) {
        if (diasProjeto > status.condicao_dias_max) {
          match = false;
        }
      }

      if (match) {
        return status;
      }
    }

    return { nome: '', cor_classe: '' };
  };

  const months = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' },
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  if (loading && projects.length === 0) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">{suspensos ? 'Projetos Suspensos' : 'Projetos'}</h1>
          <p className="text-muted-foreground">
            {suspensos
              ? 'Projetos pausados. Ao retomar, o projeto volta para a página Projetos.'
              : 'Gerencie seus projetos de crédito rural'}
          </p>
        </div>
        {!suspensos && (
          <Button onClick={() => navigate('/projetos/novo')} data-testid="new-project-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Projeto
          </Button>
        )}
      </div>

      {/* Barra de Busca Visível */}
      <Card>
        <CardContent className="py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou CPF..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Projects Card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <CardTitle className="text-lg">{suspensos ? 'Projetos Suspensos' : 'Projetos em Andamento'}</CardTitle>
              <Badge variant="secondary">{projects.length} projetos</Badge>
              
              {/* View Mode Toggle */}
              {canViewKanban && (
                <Tabs value={viewMode} onValueChange={setViewMode} className="hidden sm:block">
                  <TabsList className="h-8">
                    <TabsTrigger value="table" className="h-7 px-3 gap-1" data-testid="view-table">
                      <LayoutList className="w-4 h-4" />
                      <span className="hidden md:inline">Tabela</span>
                    </TabsTrigger>
                    <TabsTrigger value="kanban" className="h-7 px-3 gap-1" data-testid="view-kanban">
                      <Kanban className="w-4 h-4" />
                    <span className="hidden md:inline">Kanban</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Mobile View Toggle */}
              {canViewKanban && (
                <div className="sm:hidden flex items-center gap-1 border rounded-lg p-1">
                  <Button
                    variant={viewMode === 'table' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => setViewMode('table')}
                  >
                    <LayoutList className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'kanban' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => setViewMode('kanban')}
                  >
                    <Kanban className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtros
                {showFilters ? <ChevronDown className="w-4 h-4 ml-2" /> : <ChevronRight className="w-4 h-4 ml-2" />}
              </Button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 pt-4 border-t mt-4">
              <div className="space-y-2">
                <Label>Mês</Label>
                <Select
                  value={filters.mes ? String(filters.mes) : 'all'}
                  onValueChange={(v) => setFilters({ ...filters, mes: v === 'all' ? null : parseInt(v) })}
                >
                  <SelectTrigger data-testid="filter-month">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={String(m.value)}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ano</Label>
                <Select
                  value={filters.ano ? String(filters.ano) : 'all'}
                  onValueChange={(v) => setFilters({ ...filters, ano: v === 'all' ? null : parseInt(v) })}
                >
                  <SelectTrigger data-testid="filter-year">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {years.map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Etapa</Label>
                <Select
                  value={filters.etapa_id || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, etapa_id: v === 'all' ? null : v })}
                >
                  <SelectTrigger data-testid="filter-etapa">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {etapas.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Responsável</Label>
                <Select
                  value={filters.usuario_id || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, usuario_id: v === 'all' ? null : v })}
                >
                  <SelectTrigger data-testid="filter-responsavel">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {usuarios.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Com Pendência</Label>
                <Select
                  value={filters.pendencia === true ? 'sim' : filters.pendencia === false ? 'nao' : 'all'}
                  onValueChange={(v) => setFilters({ ...filters, pendencia: v === 'sim' ? true : v === 'nao' ? false : null })}
                >
                  <SelectTrigger data-testid="filter-pendencia">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="sim">Sim</SelectItem>
                    <SelectItem value="nao">Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {viewMode === 'kanban' ? (
            <KanbanBoard
              projects={projects}
              etapas={etapas}
              onUpdate={fetchData}
            />
          ) : (
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-10"></TableHead>
                    <SortableHead campo="cliente_nome">Cliente</SortableHead>
                    <SortableHead campo="cliente_cpf" className="hidden sm:table-cell">CPF</SortableHead>
                    <SortableHead campo="tipo_projeto" className="hidden sm:table-cell">Tipo</SortableHead>
                    <SortableHead campo="valor_credito" className="hidden md:table-cell">Valor Crédito</SortableHead>
                    <SortableHead campo="etapa_atual_nome">Etapa</SortableHead>
                    <SortableHead campo="usuario_nome" className="hidden lg:table-cell">Responsável</SortableHead>
                    <SortableHead campo="duracao" className="hidden lg:table-cell">Duração</SortableHead>
                    <SortableHead campo="status" className="w-20">Status</SortableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        {suspensos ? 'Nenhum projeto suspenso' : 'Nenhum projeto encontrado'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    projects
                      .filter(p => {
                        if (!searchTerm || searchTerm.trim() === '') return true;
                        const term = searchTerm.toLowerCase().trim();
                        const nome = (p.cliente_nome || '').toLowerCase();
                        const cpf = (p.cliente_cpf || '').replace(/\D/g, '');
                        const cpfBusca = term.replace(/\D/g, '');
                        const numero = (p.numero_interno || '').toLowerCase();
                        if (numero.includes(term)) return true;
                        
                        // Busca por nome OU por CPF
                        const matchNome = nome.includes(term);
                        const matchCpf = cpfBusca.length > 0 && cpf.includes(cpfBusca);
                        
                        return matchNome || matchCpf;
                      })
                      .slice()
                      .sort((a, b) => {
                        if (!sortBy) return 0;
                        // Resolve o valor de cada campo (alguns são calculados)
                        const getVal = (p) => {
                          if (sortBy === 'duracao') {
                            return p.data_inicio ? calculateDuration(p.data_inicio) : 0;
                          }
                          if (sortBy === 'etapa_atual_nome') {
                            return (p.etapa_atual_nome || p.etapa_atual || '').toString();
                          }
                          return p[sortBy];
                        };
                        const va = getVal(a);
                        const vb = getVal(b);
                        // null/undefined sempre vão para o fim
                        if (va == null && vb == null) return 0;
                        if (va == null) return 1;
                        if (vb == null) return -1;
                        let cmp;
                        if (typeof va === 'number' && typeof vb === 'number') {
                          cmp = va - vb;
                        } else {
                          cmp = String(va).localeCompare(String(vb), 'pt-BR', { sensitivity: 'base', numeric: true });
                        }
                        return sortDir === 'asc' ? cmp : -cmp;
                      })
                      .map((project) => {
                      const projectStatus = getProjectStatus(project);
                      // Mapeamento de cores para classes Tailwind
                      const colorMap = {
                        'orange': 'bg-amber-50',
                        'red': 'bg-red-50',
                        'blue': 'bg-blue-50',
                        'green': 'bg-emerald-50',
                        'yellow': 'bg-yellow-50',
                        'purple': 'bg-purple-50',
                        'pink': 'bg-pink-50',
                        'cyan': 'bg-cyan-50',
                        'gray': 'bg-gray-100',
                        'white': '',
                      };
                      const rowBgClass = colorMap[projectStatus.cor] || '';
                      return (
                      <React.Fragment key={project.id}>
                        <TableRow
                          className={cn(
                            'cursor-pointer transition-colors',
                            expandedProject === project.id && 'bg-muted/70',
                            destaqueUrl === project.id && 'ring-2 ring-inset ring-cyan-400',
                            rowBgClass,
                            !rowBgClass && 'hover:bg-muted/50'
                          )}
                          onClick={() => setExpandedProject(expandedProject === project.id ? null : project.id)}
                          data-testid={`project-row-${project.id}`}
                        >
                          <TableCell>
                            {expandedProject === project.id ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {exibirNumero && project.numero_interno && (
                                <span
                                  className="font-mono text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0"
                                  data-testid={`project-numero-${project.id}`}
                                >
                                  {project.numero_interno}
                                </span>
                              )}
                              {project.cliente_nome}
                              {project.tem_pendencia && (
                                <AlertTriangle className="w-4 h-4 text-amber-500" />
                              )}
                              {atividadesCount[project.id] > 0 && (
                                <Badge
                                  variant="outline"
                                  className="h-5 px-1.5 text-[10px] border-primary text-primary gap-1"
                                  title={`${atividadesCount[project.id]} atividade(s) pendente(s)`}
                                  data-testid={`atividade-badge-${project.id}`}
                                >
                                  <ClipboardList className="w-3 h-3" />
                                  {atividadesCount[project.id]}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            {formatCPF(project.cliente_cpf)}
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <span className="text-xs">{project.tipo_projeto || '-'}</span>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {formatCurrency(project.valor_credito)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="whitespace-nowrap">
                              {project.etapa_atual_nome || project.etapa_atual || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            <div className="flex items-center gap-2 text-sm">
                              <User className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                              <span className="truncate max-w-[100px]" title={project.usuario_nome}>
                                {project.usuario_nome || '-'}
                              </span>
                              {canTransfer && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-2 ml-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTransferProjectId(project.id);
                                    setShowTransferDialog(true);
                                  }}
                                  title="Transferir projeto"
                                >
                                  <ArrowRightLeft className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {project.data_inicio ? (
                              <span className={cn(
                                'font-medium',
                                calculateDuration(project.data_inicio) > 60 && 'text-amber-500',
                                calculateDuration(project.data_inicio) > 90 && 'text-red-500'
                              )}>
                                {calculateDuration(project.data_inicio)} dias
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            {project.status === 'desistido' ? (
                              <Badge variant="destructive" className="gap-1">
                                <XCircle className="w-3 h-3" />
                                Desistiu
                              </Badge>
                            ) : projectStatus.nome ? (
                              <Badge 
                                className={cn(
                                  "gap-1",
                                  projectStatus.cor === 'green' && "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
                                  projectStatus.cor === 'blue' && "bg-blue-500/10 text-blue-600 border-blue-500/20",
                                  projectStatus.cor === 'orange' && "bg-amber-500/10 text-amber-600 border-amber-500/20",
                                  projectStatus.cor === 'red' && "bg-red-500/10 text-red-600 border-red-500/20",
                                  projectStatus.cor === 'yellow' && "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
                                  projectStatus.cor === 'purple' && "bg-purple-500/10 text-purple-600 border-purple-500/20",
                                  projectStatus.cor === 'pink' && "bg-pink-500/10 text-pink-600 border-pink-500/20",
                                  projectStatus.cor === 'cyan' && "bg-cyan-500/10 text-cyan-600 border-cyan-500/20",
                                  projectStatus.cor === 'gray' && "bg-gray-500/10 text-gray-600 border-gray-500/20",
                                  projectStatus.cor === 'white' && "bg-gray-100 text-gray-700 border-gray-300"
                                )}
                              >
                                {projectStatus.nome}
                              </Badge>
                            ) : (
                              <Badge variant="success" className="gap-1 bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                                <FolderOpen className="w-3 h-3" />
                                Ativo
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                        {expandedProject === project.id && (
                          <TableRow>
                            <TableCell colSpan={9} className="bg-muted/30 p-0">
                              <div className="p-4">
                                <ProjectTimeline
                                  project={project}
                                  onUpdate={fetchData}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Legenda de Cores - dinâmica baseada nos status configurados */}
          {statusConfig.length > 0 && (
            <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t text-xs text-muted-foreground">
              <span className="font-medium">Legenda:</span>
              {statusConfig.map((status) => {
                const colorMap = {
                  'orange': 'bg-amber-500',
                  'red': 'bg-red-500',
                  'blue': 'bg-blue-500',
                  'green': 'bg-emerald-500',
                  'yellow': 'bg-yellow-500',
                  'purple': 'bg-purple-500',
                  'pink': 'bg-pink-500',
                  'cyan': 'bg-cyan-500',
                  'gray': 'bg-gray-500',
                  'white': 'bg-gray-200 border border-gray-300',
                };
                const bgClass = colorMap[status.cor] || 'bg-gray-300';
                return (
                  <div key={status.id || status.nome} className="flex items-center gap-1">
                    <div className={`w-3 h-3 rounded ${bgClass}`}></div>
                    <span>{status.nome}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Transferência */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transferir Projeto</DialogTitle>
            <DialogDescription>
              Selecione o novo responsável pelo projeto
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="mb-2 block">Novo Responsável</Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um usuário..." />
              </SelectTrigger>
              <SelectContent>
                {filtrarUsuariosAtribuicao(usuarios).map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleTransfer} disabled={!selectedUserId}>
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Transferir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projetos;
